import type { CSSProperties, HTMLAttributes, ReactNode } from "react";

/** The trailing spacer under the floating tab bar (DUE-358): the capsule's
 * top edge — `--d-tabbar-h` plus `--d-tabbar-lift` — and the same 40 of air a
 * bar-less screen ends with. */
export const PAD_UNDER_TAB_BAR = "calc(var(--d-tabbar-h) + var(--d-tabbar-lift) + 40px)";

export interface ScreenProps extends HTMLAttributes<HTMLDivElement> {
  /**
   * The trailing spacer — px, or a CSS length. `PAD_UNDER_TAB_BAR` clears
   * the floating tab bar; 40 is the value on a screen without one.
   * @default PAD_UNDER_TAB_BAR
   */
  pad?: number | string;
  children?: ReactNode;
}

/* Written as four longhands rather than `inset: 0` — same box, and jsdom's
 * CSSOM keeps the longhands, so the test can read them back. */
const SCROLLER: CSSProperties = {
  position: "absolute",
  /* The template's `inset: 0` on a 390x844 artboard that had no notch. The
   * app runs `viewport-fit=cover` (index.html), so `ScreenFrame` reserves the
   * status bar with `padding-top` — but an absolutely positioned box is laid
   * out against its ancestor's *padding box*, so `top: 0` would paint over
   * that reservation and put the first 47-59px of every screen (DayStrip, the
   * You header, every `Back`) under the iOS clock. Repeating the frame's
   * value here starts the scroller at the padding's inner edge instead. It
   * does not double the inset: the frame's padding never offsets an absolute
   * child. On the artboard `env()` is 0 and this is the template's box. */
  top: "var(--d-screen-pad-top, env(safe-area-inset-top))",
  right: 0,
  bottom: 0,
  left: 0,
  overflowY: "auto",
  /* The scroller carries content wider than itself on purpose — Today's glow is
   * a 780px box centred with a translate, and the stage's masks reach past the
   * gutters. The template hides that inside a 390px artboard with
   * `overflow: hidden`; ours is the full viewport, so without this the page
   * slides sideways under the thumb. */
  overflowX: "hidden",
  overscrollBehavior: "contain",
  zIndex: 1,
};

const COLUMN: CSSProperties = {
  minHeight: "100%",
  display: "flex",
  flexDirection: "column",
  alignItems: "stretch",
};

/**
 * The scrolling body of a screen, inside `ScreenFrame`. The frame owns the
 * ground, the sky, the glow and the grain and never scrolls; this owns the
 * content and does.
 *
 * The column is `min-height: 100%` and flex, which is what makes the
 * handoff's layout idiom work: a screen's main action carries
 * `margin-top: auto` and so sits at the bottom of a short screen and at the
 * end of a long one, with no sticky bar and no measuring.
 *
 * Ported from `Screen` in
 * `docs/design/handoff/design/templates/friends/friends-screen.jsx` (identical
 * in `you-screen.jsx` and `onboarding-screen.jsx` bar the `pad` default).
 */
export function Screen({
  pad = PAD_UNDER_TAB_BAR,
  className,
  style,
  children,
  ...rest
}: ScreenProps): JSX.Element {
  return (
    <div className={className} style={{ ...SCROLLER, ...style }} {...rest}>
      <div style={COLUMN}>
        {children}
        <div data-screen-spacer style={{ height: pad, flex: "none" }} />
      </div>
    </div>
  );
}

export default Screen;
